import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Loader2, LogIn, Coffee, CalendarOff, Clock, LogOut } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import UserAvatar from "@/components/app/UserAvatar";

/**
 * Live workforce status — visible to everyone. Shows how many staff are
 * currently working, on break, on leave today, yet to log in, and checked out.
 * Each card is clickable and opens a dialog listing exactly who is in that state.
 * Scoped to the selected property (super_admin sees all).
 */
export default function AttendanceLiveSummary() {
  const { selectedId } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = selectedId ? `/attendance/live-summary?property_id=${selectedId}` : "/attendance/live-summary";
    api.get(url)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const cards = [
    { key: "working", label: "Logged in", icon: LogIn, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", num: "text-emerald-700" },
    { key: "on_break", label: "On break", icon: Coffee, cls: "bg-amber-50 border-amber-200 text-amber-800", num: "text-amber-700" },
    { key: "on_leave", label: "On leave", icon: CalendarOff, cls: "bg-sky-50 border-sky-200 text-sky-800", num: "text-sky-700" },
    { key: "yet_to_login", label: "Yet to log in", icon: Clock, cls: "bg-zinc-50 border-zinc-200 text-zinc-700", num: "text-zinc-900" },
    { key: "checked_out", label: "Checked out", icon: LogOut, cls: "bg-zinc-50 border-zinc-200 text-zinc-500", num: "text-zinc-700" },
  ];

  const openCard = async (card) => {
    setActiveCard(card);
    setOpen(true);
    setMembersLoading(true);
    setMembers(null);
    try {
      const url = selectedId
        ? `/attendance/live-summary/members?property_id=${selectedId}`
        : "/attendance/live-summary/members";
      const { data: d } = await api.get(url);
      setMembers(d[card.key] || []);
    } catch (e) {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="attendance-live-summary">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <span className="label-overline">Right now</span>
          <h3 className="font-display text-base sm:text-lg font-bold tracking-tight">Who's on shift</h3>
        </div>
        {data && <span className="text-xs text-zinc-400">{data.total} team member{data.total === 1 ? "" : "s"}</span>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !data ? (
        <p className="text-sm text-zinc-400 py-4">Couldn't load live status.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {cards.map((card) => {
            const { key, label, icon: Icon, cls, num } = card;
            return (
              <button
                key={key}
                type="button"
                onClick={() => openCard(card)}
                className={`text-left border rounded-md p-3 ${cls} hover:shadow-sm hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-zinc-400`}
                data-testid={`live-${key}`}
              >
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold opacity-80">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </div>
                <div className={`text-3xl font-bold font-display mt-1 ${num}`}>{data[key]}</div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="live-members-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeCard && <activeCard.icon className="w-4 h-4" />}
              {activeCard?.label}
            </DialogTitle>
            <DialogDescription>
              {membersLoading ? "Loading…" : `${members?.length || 0} team member${(members?.length || 0) === 1 ? "" : "s"}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {membersLoading ? (
              <div className="flex items-center justify-center py-10 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : !members || members.length === 0 ? (
              <p className="text-sm text-zinc-400 py-8 text-center" data-testid="live-members-empty">No one in this state right now.</p>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {members.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 py-2.5" data-testid={`live-member-${m.id}`}>
                    <UserAvatar user={m} size={36} showRing={false} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">{m.name || "—"}</div>
                      <div className="text-xs text-zinc-500 truncate capitalize">
                        {(m.role || "").replace(/_/g, " ")}
                        {m.department ? ` · ${m.department}` : ""}
                        {m.employee_id ? ` · ${m.employee_id}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
